const { elements, SDK_FOPS } = require('../variables');
const { $t } = require('../translate');
const {
  loyaltySendOtpHtml,
  loyaltyOtpHtml,
  loyaltyVerifyingHtml,
  loyaltyAmountHtml,
  loyaltyCustomAmountHtml,
  loyaltyFailureHtml,
  loyaltyResendActiveHtml,
  loyaltySummaryHtml,
} = require('../html');
const { loyaltyOtpFetch, loyaltyReserveFetch, walletCancelFetch } = require('../http');
const { mainConfigService } = require('./config');
const { sanitizeApiData } = require('../security');


class LoyaltyService {
  constructor(documentCheckout) {
    this.documentCheckout = documentCheckout;
    this.config = null;
    this._phase = 'idle';
    this._selectedAmount = null;
    this._isFullCoverage = false;
    this._reservation = null;
    this._resendTimer = null;
    this._resendCountdown = 0;
    this._msisdn = null;        
    this._pin = null;            
    this._displayPhone = null;  
    this._onPayButtonStateChange = null;
    this._onLoyaltyAmountChange = null;
    this._onSdkStateUpdate = null;
    this._onCancelError = null;
    this._toggleInProgress = false;
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  init(loyaltyConfig, callbacks = {}) {
    this.config = loyaltyConfig;
    this._onPayButtonStateChange = callbacks.onPayButtonStateChange || null;
    this._onLoyaltyAmountChange = callbacks.onLoyaltyAmountChange || null;
    this._onSdkStateUpdate = callbacks.onSdkStateUpdate || null;
    this._onCancelError = callbacks.onCancelError || null;
  }

  // The qitaf option is on screen: a config was applied and the loyalty block rendered.
  // Unlike isActive(), this is true before the customer has touched the flow.
  isOffered() {
    return !!this.config;
  }

  isActive() {
    return this._phase !== 'idle';
  }

  isConfirmed() {
    return this._phase === 'confirmed';
  }

  isFullCoverage() {
    return this._isFullCoverage;
  }

  getSelectedAmount() {
    return this._selectedAmount;
  }

  getReservation() {
    return this._reservation;
  }

  getSubmitUrl() {
    return this.config.submit_url || null;
  }

  async handleToggle(checked) {
    if (this._toggleInProgress) return;

    if (checked) {
      this._enterSendOtpPhase();
      return;
    }

    if (this._phase !== 'confirmed') {
      this._reset();
      return;
    }

    // A confirmed redemption reserved points server-side — release them before resetting
    this._toggleInProgress = true;
    this._setToggleDisabled(true);
    try {
      await this._cancelRedemption();
    } finally {
      this._setToggleDisabled(false);
      this._toggleInProgress = false;
    }
  }

  async _cancelRedemption() {
    if (!this.config.cancel_url) {
      this._reset();
      return;
    }

    try {
      const rawRes = await walletCancelFetch(
        this.config.cancel_url,
        mainConfigService.get('apiKey'),
        this.config.code,
        this.config.session_id
      );
      const res = sanitizeApiData(rawRes);
      if (this._onSdkStateUpdate && res.sdk_setup_preload_payload) {
        this._onSdkStateUpdate(res.sdk_setup_preload_payload);
      }
      this._reset();
    } catch (err) {
      // Cancel failed server-side — stay confirmed and revert the toggle
      this._setToggleChecked(true);
      const errData = await Promise.resolve(err).catch(() => ({}));
      if (this._onCancelError) {
        this._onCancelError(errData?.detail || errData?.message || $t('Something went wrong.'));
      }
    }
  }

  // ─── Phase transitions ───────────────────────────────────────────────────────

  _enterSendOtpPhase() {
    this._clearResendTimer();
    this._phase = 'send_otp';
    this._setWrapperActive(true);
    this._renderContent(loyaltySendOtpHtml(this.config.phone || ''));
    this._bindSendOtpEvents();
    this._notifyPayButton(false);
  }

  // OTP was just requested in _sendOtp (initial send or resend) — render the
  // resend countdown that was started there.
  _enterOtpPhase() {
    this._phase = 'otp_entry';
    this._setWrapperActive(true);
    this._renderContent(loyaltyOtpHtml(this._displayPhone || this.config.phone || '', this._resendCountdown));
    this._bindOtpEvents();
    this._notifyPayButton(false);
  }

  _enterVerifyingPhase() {
    this._clearResendTimer();
    this._phase = 'otp_verifying';
    this._renderContent(loyaltyVerifyingHtml());
    this._setToggleDisabled(true);
  }

  _enterAmountPhase() {
    this._phase = 'amount_selection';
    this._selectedAmount = null;
    const { total_amount, currency_code } = this.config;
    this._renderContent(loyaltyAmountHtml(total_amount, currency_code));
    this._bindAmountEvents();
    this._notifyPayButton(false);
  }

  _enterCustomAmountPhase() {
    this._phase = 'amount_selection';
    const { total_amount } = this.config;
    this._renderContent(loyaltyCustomAmountHtml(total_amount));
    this._bindCustomAmountEvents();
  }

  _enterConfirmedPhase(reservation) {
    this._phase = 'confirmed';
    this._reservation = reservation;
    this._selectedAmount = parseFloat(reservation.walletAmount);
    this._isFullCoverage = parseFloat(reservation.remainingAmount) === 0;
    this._renderContent(loyaltySummaryHtml(
      reservation.walletAmount,
      reservation.remainingAmount,
      reservation.currencyCode || this.config.currency_code,
      this._isFullCoverage
    ));
    this._notifyPayButton(this._isFullCoverage);
    this._notifyAmountChange(reservation);
  }

  _enterFailedPhase(title, detail) {
    this._phase = 'failed';
    this._renderContent(loyaltyFailureHtml(title, detail));
    this._bindFailureEvents();
    this._notifyPayButton(false);
  }

  _reset() {
    this._clearResendTimer();
    this._phase = 'idle';
    this._selectedAmount = null;
    this._isFullCoverage = false;
    this._reservation = null;
    this._msisdn = null;
    this._pin = null;
    this._displayPhone = null;
    this.config.submit_url = null;
    this._setWrapperActive(false);
    this._hideContent();
    this._notifyPayButton(true);
    this._notifyAmountChange(null);
  }

  // ─── OTP logic ───────────────────────────────────────────────────────────────

  _bindSendOtpEvents() {
    const content = this._getContent();
    if (!content) return;

    const sendBtn = content.querySelector(`.${elements.loyaltySendOtpBtn}`);
    if (sendBtn) {
      sendBtn.addEventListener('click', () => this._sendOtp(this.config.phone || ''));
    }
  }

  async _sendOtp(msisdn) {
    this._msisdn = msisdn;

    const content = this._getContent();
    const sendBtn = content && content.querySelector(`.${elements.loyaltySendOtpBtn}`);
    if (sendBtn) {
      sendBtn.disabled = true;
      sendBtn.innerHTML = `<span class="ottu__sdk-loyalty-btn-spinner"></span>${$t('Sending...')}`;
    }

    try {
      const rawOtpRes = await loyaltyOtpFetch(
        this.config.otp_url,
        mainConfigService.get('apiKey'),
        this.config.code,
        this.config.session_id,
        msisdn
      );
      const res = sanitizeApiData(rawOtpRes);
      const countdown = res.expires_in_seconds;
      this._displayPhone = res.msisdn_last4 ? `****${res.msisdn_last4}` : msisdn;
      this._startResendCountdown(countdown);
      this._enterOtpPhase();
    } catch (err) {
      if (sendBtn) {
        sendBtn.disabled = false;
        sendBtn.innerHTML = $t('Send OTP');
      }
      const errData = await Promise.resolve(err).catch(() => ({}));
      this._enterFailedPhase($t('Unable to Send Verification Code'), errData?.detail);
    }
  }

  _bindOtpEvents() {
    const content = this._getContent();
    if (!content) return;

    const boxes = content.querySelectorAll(`.${elements.loyaltyOtpInput}`);
    boxes.forEach((box, i) => {
      box.addEventListener('input', (e) => this._handleOtpInput(e, boxes, i));
      box.addEventListener('keydown', (e) => this._handleOtpKeydown(e, boxes, i));
      box.addEventListener('paste', (e) => this._handleOtpPaste(e, boxes));
    });

    const skipBtn = content.querySelector(`.${elements.loyaltySkipBtn}`);
    if (skipBtn) {
      skipBtn.addEventListener('click', () => this._reset());
    }

    const resendBtn = content.querySelector(`.${elements.loyaltyResendBtn}`);
    if (resendBtn) {
      resendBtn.addEventListener('click', () => this._resendOtp(), { once: true });
    }

    const changeBtn = content.querySelector(`.${elements.loyaltyChangeBtn}`);
    if (changeBtn) {
      changeBtn.addEventListener('click', () => this._handleChangePhone());
    }
  }

  _handleOtpInput(e, boxes, index) {
    const val = e.target.value.replace(/\D/g, '');
    e.target.value = val ? val[0] : '';

    this._clearOtpError(boxes);

    if (val && index < boxes.length - 1) {
      boxes[index + 1].focus();
    }

    if (this._getOtpValue(boxes).length === 4) {
      this._submitOtp(this._getOtpValue(boxes));
    }
  }

  _handleOtpKeydown(e, boxes, index) {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      boxes[index - 1].focus();
      boxes[index - 1].value = '';
    }
  }

  _handleOtpPaste(e, boxes) {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '');
    text.split('').slice(0, 4).forEach((char, i) => {
      if (boxes[i]) boxes[i].value = char;
    });
    const filled = text.length;
    if (filled > 0 && filled <= 4) boxes[Math.min(filled, 3)].focus();
    if (filled >= 4) this._submitOtp(text.slice(0, 4));
  }

  _getOtpValue(boxes) {
    return [...boxes].map(b => b.value).join('');
  }

  _clearOtpError(boxes) {
    boxes.forEach(b => b.classList.remove(elements.loyaltyOtpError));
  }

  _showOtpError(boxes) {
    boxes.forEach(b => b.classList.add(elements.loyaltyOtpError));
  }

  _enterOtpErrorPhase() {
    this._clearResendTimer();
    this._pin = null;
    this._phase = 'otp_entry';
    this._renderContent(loyaltyOtpHtml(this._displayPhone || this.config.phone || '', 0, true));
    this._bindOtpEvents();
    const content = this._getContent();
    if (content) {
      const boxes = content.querySelectorAll(`.${elements.loyaltyOtpInput}`);
      this._showOtpError(boxes);
    }
  }

  // OTP entered — stop the resend timer, store the pin and advance to amount
  // selection. The pin is validated later, together with the reserve call.
  _submitOtp(otp) {
    this._clearResendTimer();
    this._pin = otp;
    this._enterAmountPhase();
  }

  async _resendOtp() {
    await this._sendOtp(this._msisdn || this.config.phone || '');
  }

  _handleChangePhone() {
    this._pin = null;
    this._msisdn = null;
    this._displayPhone = null;
    this._enterSendOtpPhase();
  }

  // ─── Amount selection logic ──────────────────────────────────────────────────

  _bindAmountEvents() {
    const content = this._getContent();
    if (!content) return;

    const chips = content.querySelectorAll(`.${elements.loyaltyAmountChip}`);
    chips.forEach((chip) => {
      chip.addEventListener('click', () => this._handleChipSelect(chip, chips));
    });
  }

  _handleChipSelect(chip, chips) {
    const rawAmount = chip.dataset.amount;

    if (rawAmount === 'custom') {
      this._enterCustomAmountPhase();
      return;
    }

    chips.forEach(c => c.classList.remove(elements.loyaltyChipSelected));
    chip.classList.add(elements.loyaltyChipSelected);

    this._selectedAmount = Math.min(Math.ceil(parseFloat(rawAmount)), this.config.total_amount || 0);
    this._confirmRedemption();
  }

  _bindCustomAmountEvents() {
    const content = this._getContent();
    if (!content) return;

    const input = content.querySelector(`.${elements.loyaltyCustomInput}`);
    const applyBtn = content.querySelector(`.${elements.loyaltyApplyCustomBtn}`);
    const errorEl = content.querySelector(`.${elements.loyaltyCustomError}`);
    const field = content.querySelector('.ottu__sdk-loyalty-custom-field');
    const cancelBtn = content.querySelector(`.${elements.loyaltyCancelCustomBtn}`);
    const actionsDiv = content.querySelector('.ottu__sdk-loyalty-custom-actions');

    if (input) {
      let debounceTimer = null;
      input.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() =>
          this._handleCustomInput(input, applyBtn, errorEl, field, actionsDiv)
        , 300);
      });
    }

    if (applyBtn) {
      applyBtn.addEventListener('click', () => {
        const amount = parseInt(input.value, 10);
        if (!isNaN(amount) && amount > 0) {
          this._selectedAmount = Math.min(amount, this.config.total_amount || 0);
          this._confirmRedemption();
        }
      });
    }

    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => this._enterAmountPhase());
    }
  }

  _handleCustomInput(input, applyBtn, errorEl, field, actionsDiv) {
    const value = input.value.trim();
    const max = Math.ceil(this.config.total_amount || 0);
    const amount = parseInt(value, 10);

    field.classList.remove('ottu__sdk-loyalty-custom-field--error');
    errorEl.classList.add('d-none');
    errorEl.textContent = '';
    applyBtn.classList.add('d-none');
    actionsDiv.classList.remove('ottu__sdk-loyalty-has-apply');

    if (value === '') return;

    if (isNaN(amount) || amount <= 0 || String(amount) !== value) {
      field.classList.add('ottu__sdk-loyalty-custom-field--error');
      errorEl.textContent = $t('Please enter a whole number');
      errorEl.classList.remove('d-none');
    } else if (amount > max) {
      field.classList.add('ottu__sdk-loyalty-custom-field--error');
      errorEl.textContent = `${$t('Maximum amount:')} ${max}`;
      errorEl.classList.remove('d-none');
    } else {
      applyBtn.classList.remove('d-none');
      actionsDiv.classList.add('ottu__sdk-loyalty-has-apply');
    }
  }

  async _confirmRedemption() {
    this._enterVerifyingPhase();

    try {
      const rawReserveRes = await loyaltyReserveFetch(
        this.config.reserve_url,
        mainConfigService.get('apiKey'),
        this.config.code,
        this.config.session_id,
        this._msisdn,
        this._pin,
        this._selectedAmount
      );
      const res = sanitizeApiData(rawReserveRes);
      const qitafEntry = res.sdk_setup_preload_payload?.payment_services?.find(s => s.flow === SDK_FOPS.QITAF);
      if (qitafEntry?.submit_url) this.config.submit_url = qitafEntry.submit_url;

      const reservation = this._buildReservation(res, qitafEntry);

      // Must precede _enterConfirmedPhase: that notifies the host, which lays out the
      // billing summary from the SDK config. Applying the payload after would show it
      // the pre-redemption order.
      if (this._onSdkStateUpdate && res.sdk_setup_preload_payload) {
        this._onSdkStateUpdate(res.sdk_setup_preload_payload);
      }

      this._setToggleDisabled(false);
      this._enterConfirmedPhase(reservation);
    } catch (err) {
      this._setToggleDisabled(false);
      const errData = await Promise.resolve(err).catch(() => ({}));
      if (errData?.error_type === 'otp') {
        this._enterOtpErrorPhase();
      } else {
        this._enterFailedPhase($t('Loyalty Redemption Failed'), errData?.detail || '');
      }
    }
  }

  // Sub-total and fee come from the qitaf service entry rather than the payload's
  // top-level `billing`, which the backend rewrites to what is *left* to pay. The
  // service entry — and each method's matching `wallet_billing` block — keeps the
  // pre-redemption order total.
  _buildReservation(res, qitafEntry) {
    const payload  = res.sdk_setup_preload_payload;
    const reserved = qitafEntry?.wallet_reservation || {};
    const walletBilling = qitafEntry?.code
      ? payload?.payment_methods?.find(m => m.wallet_billing?.[qitafEntry.code])?.wallet_billing?.[qitafEntry.code]
      : null;
    const preRedemption = qitafEntry?.billing || walletBilling || null;

    return {
      walletAmount:    res.wallet_amount    ?? reserved.wallet_amount    ?? null,
      remainingAmount: res.remaining_amount ?? reserved.remaining_amount ?? null,
      subTotal:        preRedemption?.sub_total?.value ?? payload?.amount ?? null,
      fee:             preRedemption?.fee?.value ?? null,
      currencyCode:
        res.wallet_currency ||
        reserved.currency_code ||
        qitafEntry?.currency_code ||
        this.config.currency_code ||
        null,
    };
  }

  // ─── Failure logic ───────────────────────────────────────────────────────────

  _bindFailureEvents() {
    const content = this._getContent();
    if (!content) return;

    const tryAgainBtn = content.querySelector(`.${elements.loyaltyTryAgainBtn}`);
    if (tryAgainBtn) {
      tryAgainBtn.addEventListener('click', () => this._enterSendOtpPhase());
    }
  }

  // ─── Resend countdown ────────────────────────────────────────────────────────

  _startResendCountdown(seconds) {
    this._clearResendTimer();
    this._resendCountdown = seconds !== undefined ? seconds : 300;

    this._resendTimer = setInterval(() => {
      this._resendCountdown -= 1;
      this._updateResendUI();

      if (this._resendCountdown <= 0) {
        this._clearResendTimer();
        this._showResendButton();
      }
    }, 1000);
  }

  _clearResendTimer() {
    if (this._resendTimer) {
      clearInterval(this._resendTimer);
      this._resendTimer = null;
    }
  }

  _updateResendUI() {
    const content = this._getContent();
    if (!content) return;
    const countdownEl = content.querySelector(`.${elements.loyaltyResendCountdown}`);
    if (countdownEl) countdownEl.textContent = this._resendCountdown;
  }

  _showResendButton() {
    const content = this._getContent();
    if (!content) return;
    const resendRow = content.querySelector(`.${elements.loyaltyResendRow}`);
    if (!resendRow) return;

    resendRow.innerHTML = loyaltyResendActiveHtml();

    const resendBtn = resendRow.querySelector(`.${elements.loyaltyResendBtn}`);
    if (resendBtn) {
      resendBtn.addEventListener('click', () => this._resendOtp(), { once: true });
    }
  }

  // ─── DOM helpers ─────────────────────────────────────────────────────────────

  _getWrapper() {
    return this.documentCheckout.querySelector('#' + elements.loyaltyBlock + ' .' + elements.loyaltyWrapper);
  }

  _getContent() {
    return this.documentCheckout.querySelector('#' + elements.loyaltyContent);
  }

  _renderContent(html) {
    const content = this._getContent();
    if (!content) return;
    content.innerHTML = html;
    content.classList.remove('d-none');
  }

  _hideContent() {
    const content = this._getContent();
    if (content) {
      content.innerHTML = '';
      content.classList.add('d-none');
    }
  }

  _setToggleDisabled(disabled) {
    const wrapper = this._getWrapper();
    if (!wrapper) return;
    const toggle = wrapper.querySelector(`.${elements.loyaltyToggle}`);
    if (toggle) toggle.disabled = disabled;
  }

  _setToggleChecked(checked) {
    const wrapper = this._getWrapper();
    if (!wrapper) return;
    const toggle = wrapper.querySelector(`.${elements.loyaltyToggle}`);
    if (toggle) toggle.checked = checked;
  }

  _setWrapperActive(active) {
    const wrapper = this._getWrapper();
    if (!wrapper) return;
    if (active) {
      wrapper.classList.add(elements.loyaltyActive);
    } else {
      wrapper.classList.remove(elements.loyaltyActive);
      const toggle = wrapper.querySelector(`.${elements.loyaltyToggle}`);
      if (toggle) toggle.checked = false;
    }
  }

  // ─── Callbacks ───────────────────────────────────────────────────────────────

  _notifyPayButton(shouldEnable) {
    if (this._onPayButtonStateChange) {
      this._onPayButtonStateChange(shouldEnable);
    }
  }

  _notifyAmountChange(amount) {
    if (this._onLoyaltyAmountChange) {
      this._onLoyaltyAmountChange(amount);
    }
  }
}

module.exports = { LoyaltyService };
